import { useState } from "react";
import { useTranslation } from "react-i18next";

interface CoOwnershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "open" | "interest";
  defaultAllIn?: number;
  campaignTitle?: string;
}

function formatYen(n: number): string {
  if (n >= 1000000) return `¥${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `¥${(n / 1000).toFixed(0)}K`;
  return `¥${n}`;
}

export default function CoOwnershipModal({
  isOpen,
  onClose,
  mode,
  defaultAllIn = 10000000,
  campaignTitle,
}: CoOwnershipModalProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Record<string, string>>({
    name: "",
    email: "",
    country: "",
    notes: "",
    coOwnerCount: "2",
    estimatedAllIn: String(defaultAllIn),
    sharePreference: "",
    message: "",
    agreement: "false",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const allInNum = parseInt(form.estimatedAllIn.replace(/[^0-9]/g, ""), 10) || 0;
  const countNum = parseInt(form.coOwnerCount, 10) || 1;
  const perShare = Math.round(allInNum / countNum);

  const update = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = t("co.modal.nameError");
    if (!form.email.trim()) next.email = t("co.modal.emailError");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = t("co.modal.emailInvalid");
    if (!form.country.trim()) next.country = t("co.modal.countryError");
    if (mode === "open") {
      if (!form.estimatedAllIn.trim()) next.estimatedAllIn = t("co.modal.allInError");
      if (countNum < 2 || countNum > 5) next.coOwnerCount = t("co.modal.countError");
    }
    if (mode === "interest" && !form.sharePreference.trim()) {
      next.sharePreference = t("co.modal.preferenceError");
    }
    if (form.agreement !== "true") {
      next.agreement = t("co.modal.agreementError");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) setSubmitted(true);
  };

  const guardClass =
    "border-l-4 border-vermilion bg-[#fbeee6] p-4 text-xs leading-relaxed text-ink/70";

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-ink/60 p-4 pt-12 pb-12">
      <div className="relative w-full max-w-lg bg-paper border border-hairline shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-ink/40 hover:text-ink transition-colors cursor-pointer"
          aria-label="Close"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        <div className="p-6 md:p-8">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-green-600 text-xl"></i>
              </div>
              <h3 className="font-serif text-xl text-ink mb-2">
                {mode === "open" ? t("co.modal.successOpen") : t("co.modal.successInterest")}
              </h3>
              <p className="text-sm text-ink/50 leading-relaxed max-w-xs mx-auto">
                {t("co.modal.successText")}
              </p>
              <button
                onClick={onClose}
                className="mt-6 inline-flex items-center gap-2 bg-ink text-paper text-sm font-medium px-6 py-2.5 rounded-full hover:bg-indigo transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("btn.close")}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <h3 className="font-serif text-xl text-ink mb-1">
                {mode === "open"
                  ? t("co.modal.openTitle")
                  : campaignTitle
                    ? t("co.modal.interestTitle", { title: campaignTitle })
                    : t("co.modal.interestTitle", { title: "" })}
              </h3>
              <p className="text-xs text-ink/40 mb-5">
                {t("co.modal.subtitle")}
              </p>

              <div className={guardClass + " mb-6"}>
                <strong className="text-ink/90 block mb-1">{t("co.modal.guardrailTitle")}</strong>
                {t("co.modal.guardrailText")}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-ink/60 mb-1">{t("co.modal.name")}</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    className="w-full px-3 py-2 border border-hairline bg-white text-sm text-ink focus:outline-none focus:border-gold transition-colors"
                  />
                  {errors.name && <p className="text-xs text-vermilion mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-ink/60 mb-1">{t("co.modal.email")}</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className="w-full px-3 py-2 border border-hairline bg-white text-sm text-ink focus:outline-none focus:border-gold transition-colors"
                  />
                  {errors.email && <p className="text-xs text-vermilion mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-ink/60 mb-1">{t("co.modal.country")}</label>
                  <input
                    type="text"
                    value={form.country}
                    onChange={(e) => update("country", e.target.value)}
                    className="w-full px-3 py-2 border border-hairline bg-white text-sm text-ink focus:outline-none focus:border-gold transition-colors"
                  />
                  {errors.country && <p className="text-xs text-vermilion mt-1">{errors.country}</p>}
                </div>

                {mode === "open" && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-ink/60 mb-1">{t("co.modal.coOwnerCount")}</label>
                        <select
                          value={form.coOwnerCount}
                          onChange={(e) => update("coOwnerCount", e.target.value)}
                          className="w-full px-3 py-2 border border-hairline bg-white text-sm text-ink focus:outline-none focus:border-gold transition-colors cursor-pointer"
                        >
                          {[2, 3, 4, 5].map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                        {errors.coOwnerCount && (
                          <p className="text-xs text-vermilion mt-1">{errors.coOwnerCount}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-ink/60 mb-1">{t("co.modal.estimatedAllIn")}</label>
                        <input
                          type="text"
                          value={form.estimatedAllIn}
                          onChange={(e) => update("estimatedAllIn", e.target.value)}
                          className="w-full px-3 py-2 border border-hairline bg-white text-sm text-ink focus:outline-none focus:border-gold transition-colors"
                        />
                        {errors.estimatedAllIn && (
                          <p className="text-xs text-vermilion mt-1">{errors.estimatedAllIn}</p>
                        )}
                      </div>
                    </div>

                    <div className="bg-paper-secondary border border-hairline p-3">
                      <p className="text-xs text-ink/50">
                        {t("co.modal.shareCalc")}{" "}
                        <span className="font-semibold text-ink">{formatYen(perShare)}</span>
                      </p>
                      <p className="text-[10px] text-ink/30 mt-1">
                        {t("co.modal.shareCalcNote", {
                          allIn: formatYen(allInNum),
                          count: countNum,
                          share: formatYen(perShare),
                        })}
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-ink/60 mb-1">{t("co.modal.notes")}</label>
                      <textarea
                        value={form.notes}
                        onChange={(e) => update("notes", e.target.value)}
                        rows={3}
                        maxLength={500}
                        className="w-full px-3 py-2 border border-hairline bg-white text-sm text-ink focus:outline-none focus:border-gold transition-colors resize-none"
                      />
                    </div>
                  </>
                )}

                {mode === "interest" && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-ink/60 mb-1">{t("co.modal.sharePreference")}</label>
                      <select
                        value={form.sharePreference}
                        onChange={(e) => update("sharePreference", e.target.value)}
                        className="w-full px-3 py-2 border border-hairline bg-white text-sm text-ink focus:outline-none focus:border-gold transition-colors cursor-pointer"
                      >
                        <option value="">{t("option.select")}</option>
                        <option value="full">{t("co.modal.shareFull")}</option>
                        <option value="half">{t("co.modal.shareHalf")}</option>
                        <option value="quarter">{t("co.modal.shareQuarter")}</option>
                        <option value="flexible">{t("co.modal.shareFlexible")}</option>
                      </select>
                      {errors.sharePreference && (
                        <p className="text-xs text-vermilion mt-1">{errors.sharePreference}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-ink/60 mb-1">{t("co.modal.message")}</label>
                      <textarea
                        value={form.message}
                        onChange={(e) => update("message", e.target.value)}
                        rows={3}
                        maxLength={500}
                        className="w-full px-3 py-2 border border-hairline bg-white text-sm text-ink focus:outline-none focus:border-gold transition-colors resize-none"
                      />
                    </div>
                  </>
                )}

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.agreement === "true"}
                    onChange={(e) => update("agreement", e.target.checked ? "true" : "false")}
                    className="mt-0.5 cursor-pointer"
                  />
                  <span className="text-xs text-ink/60 leading-relaxed">
                    {t("co.modal.agreementText")}
                  </span>
                </label>
                {errors.agreement && <p className="text-xs text-vermilion">{errors.agreement}</p>}
              </div>

              <button
                type="submit"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-vermilion text-paper text-sm font-medium px-6 py-3 hover:bg-vermilion/90 transition-colors cursor-pointer whitespace-nowrap"
              >
                {mode === "open" ? t("btn.submitListingRequest") : t("btn.registerInterest")}
                <i className="ri-arrow-right-line"></i>
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}